<template>
  <div class="nutrition-table-wrapper">
    <div class="table-responsive">
      <table class="table table-bordered table-striped align-middle nutrition-table">
        <thead>
          <tr>
            <th class="nutrient-col">Nutriment</th>
            <th
              v-for="member in members"
              :key="member.id"
              class="text-center"
            >
              {{ member.name }}
            </th>
          </tr>
        </thead>
        <tbody>
          <!-- Nutriments -->
          <tr>
            <td colspan="100%" class="section-title">Couverture des besoins nutritionnels</td>
          </tr>
          <tr v-for="row in nutrientRows" :key="row.name">
            <th>
              {{ row.title }}<span v-if="row.name === 'vitamin_d'" class="vitamin-d-asterisk">*</span>
              <span v-if="row.unit" class="unit"> ({{ row.unit }}/j)</span>
            </th>
            <td
              v-for="member in members"
              :key="member.id"
              class="text-center"
              :class="statusClass(row.members[member.id])"
            >
              <NutritionCell :entry="row.members[member.id]" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="legend">
      <span class="legend-item status-ok">OK</span>
      <span class="legend-item status-moyen">Moyen</span>
      <span class="legend-item status-insuffisant">Insuffisant</span>
      <span class="legend-item status-trop-eleve">Trop élevé</span>
    </div>

    <p class="vitamin-d-note">
      * Un complément alimentaire en vitamine D peut être nécessaire, notamment en hiver&nbsp;;
      demandez conseil à votre pharmacien.
    </p>
  </div>
</template>

<script>
const NutritionCell = {
  name: 'NutritionCell',
  props: {
    entry: {
      type: Object,
      default: null,
    },
  },
  render(h) {
    if (!this.entry || this.entry.actual === undefined || this.entry.actual === null) {
      return h('span', { class: 'muted' }, '—');
    }

    const { actual, unit, percentage, status } = this.entry;

    const children = [
      h('span', { class: 'value' }, `${formatNumber(actual)}${unit ? ' ' + unit : ''}`),
    ];

    if (percentage !== null && percentage !== undefined) {
      children.push(h('span', { class: 'percentage' }, ` (${percentage} %)`));
    }

    if (status) {
      children.push(h('span', { class: 'status-icon' }, ` ${statusIcon(status)}`));
    }

    return h('span', children);
  },
};

function formatNumber(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function statusIcon(status) {
  switch (status) {
    case 'OK':
      return '✅';
    case 'Moyen':
      return '🟡';
    case 'Insuffisant':
      return '🔻';
    case 'Trop élevé':
      return '🔺';
    default:
      return '';
  }
}

export default {
  name: 'NutritionTable',
  components: { NutritionCell },
  props: {
    household: {
      type: Array,
      default: () => [],
    },
    members: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    nutrientRows() {
      // Ordre de référence = ordre des nutriments du foyer (hors PGL)
      const order = this.household
        .filter((n) => n.name !== 'protein_glucides_lipides')
        .map((n) => n.name);

      return order.map((name) => {
        const foyerEntry = this.household.find((n) => n.name === name) || null;

        const membersEntries = {};
        this.members.forEach((member) => {
          membersEntries[member.id] = (member.nutrients || []).find((n) => n.name === name) || null;
        });

        return {
          name,
          title: (foyerEntry && foyerEntry.title) || name,
          unit: foyerEntry && foyerEntry.unit,
          members: membersEntries,
        };
      });
    },
  },
  methods: {
    statusClass(entry) {
      if (!entry || !entry.status) return '';

      switch (entry.status) {
        case 'OK':
          return 'status-ok';
        case 'Moyen':
          return 'status-moyen';
        case 'Insuffisant':
          return 'status-insuffisant';
        case 'Trop élevé':
          return 'status-trop-eleve';
        default:
          return '';
      }
    },
  },
};
</script>

<style scoped>
.nutrition-table-wrapper {
  margin-bottom: 24px;
}

.nutrition-table {
  border-radius: 14px;
  overflow: hidden;
  font-size: 14px;
}

.nutrition-table th,
.nutrition-table td {
  vertical-align: middle;
}

.nutrient-col {
  min-width: 160px;
}

.unit {
  font-weight: 400;
  font-size: 12px;
  color: #6c757d;
}

.section-title {
  background-color: #f1f6e9;
  font-weight: 700;
  color: #2e7d32;
  text-align: left;
}

.muted {
  color: #adb5bd;
}

.value {
  font-weight: 600;
}

.percentage {
  color: #495057;
  font-size: 13px;
}

.status-icon {
  font-size: 13px;
}

.status-ok {
  background-color: rgba(46, 125, 50, 0.08);
}

.status-moyen {
  background-color: rgba(255, 193, 7, 0.12);
}

.status-insuffisant {
  background-color: rgba(198, 40, 40, 0.08);
}

.status-trop-eleve {
  background-color: rgba(198, 40, 40, 0.16);
}

.legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 10px;
  font-size: 13px;
}

.legend-item {
  padding: 4px 10px;
  border-radius: 6px;
}

.legend-item.status-ok {
  background-color: rgba(46, 125, 50, 0.08);
  color: #2e7d32;
}

.legend-item.status-moyen {
  background-color: rgba(255, 193, 7, 0.12);
  color: #8a6d00;
}

.legend-item.status-insuffisant {
  background-color: rgba(198, 40, 40, 0.08);
  color: #c62828;
}

.legend-item.status-trop-eleve {
  background-color: rgba(198, 40, 40, 0.16);
  color: #c62828;
}

.vitamin-d-asterisk {
  color: #2e7d32;
  font-weight: 700;
  margin-left: 1px;
}

.vitamin-d-note {
  margin-top: 10px;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
}
</style>
